import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAgentsApi } from '../apis/agents-apis';
import { Agent } from '../types';
import { Search, ChevronUp, Plus, Eye, Trash2, Loader2, X } from 'lucide-react';
import _ from 'lodash';

import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Card, CardContent } from "../components/ui/Card";
import { Spinner } from "../components/ui/Spinner";
import { ConfirmationDialog } from '../components/ConfirmationDialog';
import { useToast } from "../components/ui/Toast";

export const AgentsList: React.FC = () => {
  const initialLoadRef = useRef(false);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [displayedAgents, setDisplayedAgents] = useState<Agent[]>([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<Agent[] | null>(null);
  
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [agentToDelete, setAgentToDelete] = useState<string | null>(null);
  const [isDeletingId, setIsDeletingId] = useState<string | null>(null);

  const agentsApi = useAgentsApi();

  const { addToast } = useToast();
  const navigate = useNavigate();
  const ITEMS_PER_PAGE = 20;

  const updateDisplayedAgents = useCallback((currentPage: number, agentsArray: Agent[]) => {
    const startIndex = 0;
    const endIndex = currentPage * ITEMS_PER_PAGE;
    setDisplayedAgents(agentsArray.slice(startIndex, endIndex));
    setPage(currentPage);
  }, []);

  const loadMoreAgents = useCallback(() => {
    if (!searchTerm && !loading) {
      const nextPage = page + 1;
      updateDisplayedAgents(nextPage, agents);
    }
  }, [searchTerm, loading, page, agents, updateDisplayedAgents]);

  const fetchAgents = useCallback(async () => {
    if (loading || initialLoadRef.current) return;
    
    setLoading(true);
    try {
      initialLoadRef.current = true;
      const getAllAgents = false;
      const fetchedAgents = await agentsApi.getAgents(getAllAgents);
      setAgents(fetchedAgents);
      setTotalCount(fetchedAgents.length);
      updateDisplayedAgents(1, fetchedAgents);
    } catch (error) {
      console.error('Error fetching agents:', error);
      addToast({
        title: "Error",
        description: "Failed to retrieve agents. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [loading, updateDisplayedAgents, addToast]);

  const handleSearch = _.debounce((term: string) => {
    setSearchTerm(term);
    if (term) {
      const results = agents.filter(agent =>
        agent.agentName.toLowerCase().includes(term.toLowerCase()) ||
        agent.agentDescription?.toLowerCase().includes(term.toLowerCase()) ||
        agent.agentTags?.some(tag => tag.toLowerCase().includes(term.toLowerCase()))
      );
      setSearchResults(results);
    } else {
      setSearchResults(null);
      updateDisplayedAgents(1, agents);
    }
  }, 300);

  const confirmDelete = (id: string) => {
    setAgentToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirmed = async () => {
    if (agentToDelete) {
      setIsDeletingId(agentToDelete);
      try {
        await agentsApi.deleteAgent(agentToDelete);
        addToast({
          title: "Success",
          description: "Agent successfully deleted",
          variant: "default",
        });
        initialLoadRef.current = false; // Reset the ref to allow a new fetch
        fetchAgents();
      } catch (error) {
        addToast({
          title: "Error",
          description: "Failed to delete agent. Please try again.",
          variant: "destructive",
        });
      } finally {
        setIsDeletingId(null);
        setDeleteDialogOpen(false);
        setAgentToDelete(null);
      }
    }
  };

  const handleView = (agent: Agent) => {
    navigate(`/studio/agents/edit/${agent.agentId}`);
  };

  useEffect(() => {
    fetchAgents();
  }, [fetchAgents]);

  useEffect(() => {
    const handleScroll = _.throttle(() => {
      setShowScrollTop(window.scrollY > 400);

      if (!searchTerm && !loading) {
        const scrolledToBottom =
          window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 800;

        if (scrolledToBottom) {
          loadMoreAgents();
        }
      }
    }, 200);

    if (initialLoadRef.current) {
      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    }
  }, [searchTerm, loading, loadMoreAgents]);

  const currentAgents = searchResults || displayedAgents;

  return (
    <div className="container mx-auto p-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Agents Management
        </h1>
        <p className="text-sm font-medium text-gray-500 text-muted-foreground mt-1 mb-6">
          Configure and manage agents for your applications.
        </p>

        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="w-full sm:max-w-md">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <Input
                type="text"
                placeholder="Search agents by name, description, or tags..."
                className="w-full pl-10 pr-8 py-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  handleSearch(e.target.value);
                }}
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => {
                    setSearchTerm('');
                    handleSearch('');
                  }}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                  aria-label="Clear search"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
          <div className="w-full sm:w-auto">
            <Button
              variant="default"
              className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white transition-colors duration-200"
              onClick={() => navigate('/studio/agents/add')}
            >
              <Plus className="mr-2 h-4 w-4" />
              Add New Agent
            </Button>
          </div>
        </div>
      </div>

      <div className="text-sm text-muted-foreground mb-4">
        {searchTerm ? (
          `Found ${searchResults?.length || 0} results for "${searchTerm}"`
        ) : (
          `Showing ${displayedAgents.length} of ${totalCount} agents`
        )}
      </div>

      <div className="grid gap-4 sm:grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
        {currentAgents.map((agent) => (
          <Card 
            key={agent.agentId} 
            className="h-full flex flex-col hover:shadow-md transition-all duration-200 border border-gray-200 dark:border-gray-700 hover:border-blue-200 dark:hover:border-blue-800 hover:shadow-blue-50 dark:hover:shadow-blue-900/20"
          >
            <CardContent className="p-4 flex flex-col flex-grow">
              <div className="flex-grow">
                <div className="flex items-start justify-between">
                  <h2 className="text-base font-semibold text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors line-clamp-2">
                    {agent.agentName}
                  </h2>
                </div>
                
                <p className="mt-2 text-gray-600 dark:text-gray-300 text-sm line-clamp-2">
                  {agent.agentDescription || 'No description provided.'}
                </p>

                {agent.agentTags && agent.agentTags.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {agent.agentTags.slice(0, 3).map((tag, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center rounded-md bg-orange-50 dark:bg-orange-900/30 px-2 py-1 text-xs font-medium text-orange-700 dark:text-orange-300 ring-1 ring-inset ring-orange-700/10 dark:ring-orange-600/30"
                      >
                        {tag}
                      </span>
                    ))}
                    {agent.agentTags.length > 3 && (
                      <div className="relative inline-block group">
                        <span className="inline-flex items-center rounded-md bg-orange-50 dark:bg-orange-900/30 px-2 py-1 text-xs font-medium text-orange-700 dark:text-orange-300 ring-1 ring-inset ring-orange-700/10 dark:ring-orange-600/30 cursor-pointer">
                          +{agent.agentTags.length - 3}
                        </span>
                        <div className="absolute left-0 z-10 mt-1 w-48 origin-top-left rounded-md bg-white dark:bg-gray-800 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none hidden group-hover:block">
                          <div className="py-1">
                            {agent.agentTags.slice(3).map((tag, index) => (
                              <span
                                key={index}
                                className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-800 flex justify-end space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleView(agent)}
                  disabled={isDeletingId === agent.agentId}
                  className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/30 border-blue-200 dark:border-blue-800"
                >
                  <Eye className="mr-2 h-4 w-4" />
                  View
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => confirmDelete(agent.agentId)}
                  disabled={isDeletingId === agent.agentId}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  {isDeletingId === agent.agentId ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Trash2 className="mr-2 h-4 w-4" />
                  )}
                  {isDeletingId === agent.agentId ? 'Deleting...' : 'Delete'}
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {loading ? (
        <div className="flex flex-col items-center my-8 gap-2">
          <Spinner className="border-blue-500" />
          <p className="text-center text-muted-foreground">Loading agents...</p>
        </div>
      ) : currentAgents.length === 0 && (
        <Card className="max-w-2xl mx-auto m-4">
          <CardContent className="p-6">
            <p className="text-center text-muted-foreground">
              {searchTerm 
                ? 'No agents found matching your search.' 
                : 'No agents found. Create a new agent to get started.'}
            </p>
            {!searchTerm && (
              <div className="mt-4 flex justify-center">
                <Button
                  onClick={() => navigate('/studio/agents/add')}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Create Agent
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <ConfirmationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleDeleteConfirmed}
        title="Delete Agent"
        description="This will permanently delete the agent from the system. This action cannot be undone."
        confirmLabel={isDeletingId ? "Deleting..." : "Delete"}
        cancelLabel="Cancel"
        disabled={Boolean(isDeletingId)}
        requireVerification={true}
        verificationWord="DELETE"
      />

      {showScrollTop && (
        <Button
          variant="secondary"
          size="icon"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-8 right-8 rounded-full shadow-lg bg-blue-500 hover:bg-blue-600 text-white"
        >
          <ChevronUp size={24} />
        </Button>
      )}
    </div>
  );
};

export default AgentsList;